import { uniq } from 'es-toolkit';

import { Level } from '@paragon/contract-library';
import { getInsertionsWithPendingChanges } from '@paragon/layout-models';
import { naturalSortByProperty, notNullish, sortBy } from '@paragon/utilities';

import { getAllBuildingsDictionary, getAllLevelsDictionary } from '../../../../store/selectors/layoutDataSelectors';
import { useAppSelector } from '../../../hooks';

export function useBuildingLevelHierarchy() {
  const buildings = useAppSelector(getAllBuildingsDictionary);
  const levels = useAppSelector(getAllLevelsDictionary);
  const insertions = useAppSelector(getInsertionsWithPendingChanges);

  /** Highest level first */
  function levelsIn(buildingGuid: string | null): Level[] {
    if (buildingGuid == null) return [];

    const levelsInBuilding = uniq(
      insertions.filter((insertion) => insertion.parentGuid === buildingGuid).map((insertion) => insertion.objectGuid),
    )
      .map((levelGuid) => levels[levelGuid])
      .filter(notNullish);

    return sortBy(levelsInBuilding, (level) => level.index, 'desc');
  }

  /** Assumes level belongs to only one building */
  function buildingContaining(levelGuid: string | null): string | null {
    if (levelGuid == null) return null;

    return (
      insertions.find((insertion) => insertion.objectGuid === levelGuid && buildings[insertion.parentGuid] != null)
        ?.parentGuid ?? null
    );
  }

  const buildingsWithLevels = naturalSortByProperty(
    Object.values(buildings).filter((building) => levelsIn(building.guid).length > 0),
    (building) => building.name,
  );

  return { buildings, levels, buildingsWithLevels, levelsIn, buildingContaining };
}
